import React, { useEffect, useState } from "react";
import { CheckCircle as CheckCircleIcon, CardGiftcard as GiftIcon, Login as LoginIcon } from "@mui/icons-material";
import Logger from "../../services/Logger";
import { Box, Button, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { TEXT } from "../../dark-theme/palette";
import ApiService from "../../services/ApiService";
import AuthService from "../../services/AuthService";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function ReferralInviteeAccept() {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inviterName, setInviterName] = useState("");
    const [event, setEvent] = useState(null);
    const [isEventOver, setIsEventOver] = useState(false);
    const [, setIsAuthenticated] = useState(false);

    useEffect(() => {
        trackEvent("entered", { name: "ReferralInviteeAccept" });
    }, []);

    useEffect(() => {
        const processInvitation = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get token and eventId from query parameters
                const token = searchParams.get("token");
                const eventId = searchParams.get("eventId");

                if (!token || !eventId) {
                    setError("Invalid invitation link. Missing token or event ID.");
                    setLoading(false);
                    return;
                }

                // Call GET /home/referral/invitee-verified with query parameters
                const acceptResponse = await ApiService.getData(`/home/referral/invitee-verified?token=${encodeURIComponent(token)}&eventId=${encodeURIComponent(eventId)}`);
                
                // Extract cloudUser and event from the response
                const cloudUser = acceptResponse?.cloudUser;
                const eventData = acceptResponse?.event;

                if (!cloudUser || !eventData) {
                    setError("Invalid response from server. Missing inviter or event information.");
                    setLoading(false);
                    return;
                }

                // Build inviter name from cloudUser
                let extractedInviterName = "";
                if (cloudUser.firstName || cloudUser.lastName) {
                    extractedInviterName = `${cloudUser.firstName || ""} ${cloudUser.lastName || ""}`.trim();
                } else if (cloudUser.email) {
                    extractedInviterName = cloudUser.email;
                } else {
                    extractedInviterName = "Unknown";
                }

                setInviterName(extractedInviterName);
                setEvent(eventData);

                // Check if event is over
                if (eventData && eventData.endAt) {
                    const now = new Date().getTime();
                    setIsEventOver(now > eventData.endAt);
                }

                // Check authentication status
                try {
                    const authStatus = await AuthService.isAuthenticated();
                    setIsAuthenticated(authStatus);
                } catch (authError) {
                    Logger.error('Error checking authentication:', authError);
                    setIsAuthenticated(false);
                }

                setLoading(false);
            } catch (err) {
                Logger.error("Error processing invitation:", err);
                setError(err.message || "Failed to process invitation. Please try again.");
                setLoading(false);
            }
        };

        processInvitation();
    }, [searchParams]);

    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2, color: TEXT.secondary }}>
                    Processing your invitation...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }}>
                <Card sx={{
                    maxWidth: 600,
                    width: '100%',
                    textAlign: 'center',
                    p: 4,
                    boxShadow: 4,
                    borderRadius: 3
                }}>
                    <Typography variant="h5" color="error" sx={{ mb: 2 }}>
                        Error
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {error}
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Check if event is over
    if (isEventOver) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }}>
                <Card sx={{
                    maxWidth: 600,
                    width: '100%',
                    textAlign: 'center',
                    p: 4,
                    boxShadow: 4,
                    borderRadius: 3
                }}>
                    <Typography variant="h5" color="error" sx={{ mb: 2, fontWeight: 600 }}>
                        Sorry, this event is over
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The referral event has ended. Please check for other active events.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // If event is active, show the invitation details
    const handleSignUp = async () => {
        try {
            await AuthService.signup();
        } catch (error) {
            Logger.error('Error redirecting to signup:', error);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            p: 3,
            pt: 6
        }}>
            <Card sx={{
                maxWidth: 600,
                width: '100%',
                boxShadow: 4,
                borderRadius: 3
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}>
                        You've Been Invited!
                    </Typography>

                    {/* Inviter Information */}
                    {inviterName && (
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Invited by
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#3BA666' }}>
                                {inviterName}
                            </Typography>
                        </Box>
                    )}

                    {/* Event Information */}
                    {event && (
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Referral Event
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                {event.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Invite {event.requiredCount} friends and get {event.rewardMonths} month{event.rewardMonths !== 1 ? 's' : ''} free subscription!
                            </Typography>
                        </Box>
                    )}

                    {/* Sign Up Button and Message */}
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(76, 175, 80, 0.16)',
                                border: '2px solid #4caf50',
                                borderRadius: 2,
                                p: 3,
                                mb: 3,
                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)'
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                                <GiftIcon sx={{ color: '#81c784', mt: 0.5, fontSize: 22 }} />
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.7,
                                        color: '#81c784',
                                        fontWeight: 500
                                    }}
                                >
                                    If you join now, <strong style={{ textDecoration: 'underline', color: '#81c784' }}>{inviterName || 'your inviter'}</strong> will get <strong style={{ textDecoration: 'underline', color: '#81c784' }}>1 month free</strong> when the requirement is completed.
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                                <CheckCircleIcon sx={{ color: '#81c784', mt: 0.5, fontSize: 20 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#81c784',
                                        lineHeight: 1.6
                                    }}
                                >
                                    Sure you can participate in this event after <strong style={{ textDecoration: 'underline', color: '#81c784' }}>signing up</strong>.
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <LoginIcon sx={{ color: '#81c784', mt: 0.5, fontSize: 20 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#81c784',
                                        fontWeight: 500,
                                        lineHeight: 1.6
                                    }}
                                >
                                    After signing up, you MUST <strong style={{ textDecoration: 'underline', color: '#81c784' }}>signin</strong> at least once to be counted.
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSignUp}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                backgroundColor: '#3BA666',
                                '&:hover': { backgroundColor: '#2e7d32' }
                            }}
                        >
                            Sign Up
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

